
import { ColourService } from "@services/colour.service";
import * as angular from "angular";
import { RadialGaugeOptions } from "canvas-gauges";
import Highcharts, { Gradient } from "highcharts";


export type ChartType = "line" | "areaspline" | "pie" | "bar" | "column" | "xrange" | "Combination chart" | "polar";
export type PointEventType = "click" | "mouseOver" | "mouseOut" | "drag" | "dragStart" | "drop" | "remove";

export interface IHighchartsDataSeries<T = [any, number]> {
    name: string;
    data: T[];
    color?: string;
}

export class ChartService {

    static $inject = ["$q", "$timeout", "colourService"];

    constructor(
        private $q: angular.IQService,
        private $timeout: angular.ITimeoutService,
        private readonly colourService: ColourService) {
    }

    getDefaultGaugeChartOptions(value: number, minValue: number, maxValue: number, highlights: CanvasGauges.Highlight[]): RadialGaugeOptions {
        //console.log("getDefaultGaugeChartOptions", value, minValue, maxValue);

        var majorTicks: string[] = [];

        // Aim for 10% intervals
        var range = maxValue - minValue;
        var tickValue = Math.ceil(range / 10);
        tickValue = Math.max(1, tickValue);
        for (var i = minValue; i <= maxValue; i += tickValue) {
            majorTicks.push(`${i}`);
        }

        return {
            renderTo: "",
            value: value,
            minValue: minValue,
            startAngle: 90 - 30,
            ticksAngle: 180 + 60,
            valueBox: false,
            animatedValue: true,
            animation: true,
            maxValue: maxValue,
            majorTicks: majorTicks,
            minorTicks: 2,
            strokeTicks: true,
            highlights: highlights,
            colorPlate: "transparent",
            borderShadowWidth: 0,
            borders: false,
            needleType: "arrow",
            needleWidth: 3,
            colorNeedle: "#555",
            colorNeedleEnd: "#333",
            needleCircleSize: 7,
            needleCircleOuter: false,
            needleCircleInner: true,
            animationDuration: 1500,
            animationRule: "linear",
            animateOnInit: true,
            //colorNeedleCircleInner: "white",
            //colorNeedleCircleInnerEnd: "gainsboro",

            highlightsWidth: 20,
            //title: "test yo",
            //fontTitle: "Times New Roman",
            fontNumbers: `"Roboto"`,
            fontNumbersSize: 25,
            //fontValue: "Times New Roman",
            //units: "Idle Average",
            //fontUnits: `"Roboto"`,
            //fontUnitsSize: 40,
            //fontUnitsWeight: "500"
        };
    }

    getAllChartsOnPage(): Highcharts.ChartObject[] {
        return window["Highcharts"].charts;
    }

    reflowAllCharts() {
        this.$timeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 50);
    }

    setXAxisType(axisOptions: Highcharts.AxisOptions | Highcharts.AxisOptions[], type: "datetime") {
        axisOptions = this.getAxisOptions(axisOptions);

        axisOptions.type = type;
    }

    addOrUpdateChartCrossHairFromMouseX(xaxis: any, mouseXPosition: number) {
        var x: number = xaxis.toValue(mouseXPosition, false);
        this.addOrUpdateChartCrossHair(xaxis, x);
    }

    addOrUpdateChartCrossHair(xaxis: any, xValue: number) {

        xaxis.removePlotLine('plot-line-x');

        if (xValue) {
            xaxis.addPlotLine({
                value: xValue,
                color: 'rgba(180,180,180,0.5)',
                width: 1,
                id: 'plot-line-x',
                zIndex: 5
            });
        }
    }

    setChartXAxisExtremes(chart: Highcharts.Options, max: number, min: number) {

        var xAxis = chart.xAxis as any;

        xAxis.max = max;
        xAxis.min = min;
    }

    setAxisLabelFormatter(axisOptions: Highcharts.AxisOptions | Highcharts.AxisOptions[], callBackFn: () => string) {
        var axisArray = axisOptions as Highcharts.AxisOptions[];

        if (!Array.isArray(axisOptions)) {
            axisArray = [axisOptions as Highcharts.AxisOptions];
        }

        axisArray.forEach(axisOption => {
            axisOption.labels = axisOption.labels || {};
            axisOption.labels.formatter = callBackFn;
        });
    }

    getAxisOptions(axisOptions: Highcharts.AxisOptions | Highcharts.AxisOptions[]): Highcharts.AxisOptions {
        if (Array.isArray(axisOptions)) {
            console.error("Dont send arrays into this function");
        }

        return axisOptions as Highcharts.AxisOptions;
    }

    setLegendOptions(options: Highcharts.Options, legend: Highcharts.LegendOptions) {
        options.legend = legend;
    }

    addYAxis(options: Highcharts.Options, newAxis: Highcharts.AxisOptions): any {
        if (!options.yAxis) {
            options.yAxis = [];
        }

        if (!options.yAxis) {
            console.error("No yAxis provided");
            return;
        }

        if (!newAxis.id) {
            newAxis.id = `yAxis${Math.round(Math.random() * 1000)}`;
        }

        (options.yAxis as Highcharts.AxisOptions[]).push(newAxis);
    }

    subscribeToMouseEvents(options: Highcharts.Options, eventName: PointEventType, callback: (x: number, clientX: number, event?: MouseEvent) => void) {
        var chartTypes = ["line", "spline", "areaspline", "xrange", "Combination chart"];

        chartTypes.forEach(chartType => {
            //if (!options.plotOptions[chartType]) {
            //    return;
            //}

            options.plotOptions[chartType] = options.plotOptions[chartType] || {};
            options.plotOptions[chartType].point = options.plotOptions[chartType].point || {};
            options.plotOptions[chartType].point.events = options.plotOptions[chartType].point.events || {};

            options.plotOptions[chartType].point.events[eventName] = function () {
                callback(this.x, this.clientX, this);
            }
        });
    }

    createDashboardChartOptions(chartType: ChartType, showSeriesLegend: boolean = true): Highcharts.Options {

        var options = this.createChartOptions(chartType, showSeriesLegend);

        // Remove xAxis
        var xAxis = options.xAxis as Highcharts.AxisOptions;
        xAxis.visible = false;
        xAxis.endOnTick = false;
        xAxis.minPadding = 0;

        var yAxis = options.yAxis as Highcharts.AxisOptions;
        yAxis.visible = false;
        yAxis.endOnTick = false;

        // Remove spacing
        options.chart.marginTop = 0;
        options.chart.marginLeft = 0;
        options.chart.spacing = [0, 0, 0, 0];
        options.chart.plotBorderWidth = 0;
        options.chart.alignTicks = false;

        return options;
    }

    setOnLoadCallback(options: Highcharts.Options, callback: (object: Highcharts.ChartObject, event: Event) => void) {

        options.chart = options.chart || {};
        options.chart.events = options.chart.events || {};

        options.chart.events.load = function (event: Event) {
            callback(this, event);
        }
    }

    createBaseChartOptions(chartType: ChartType, showSeriesLegend: boolean = true): Highcharts.Options {
        const options: Highcharts.Options = {
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            },
            title: null,
            tooltip: {},
            legend:
            {
                enabled: showSeriesLegend
            },
            plotOptions: {},
            chart: {
                type: chartType,
                backgroundColor: "transparent",
                resetZoomButton: {
                    theme: {
                        display: "none"
                    }
                }
            }
        };

        return options;
    }

    createChartOptions(chartType: ChartType, showSeriesLegend: boolean = true): Highcharts.Options {
        switch (chartType) {
            case "line":
            case "areaspline":
                return this.createLineChartOptions(showSeriesLegend);

            case "column":
                return this.createColumnChartOptions(showSeriesLegend);

            case 'polar':
                return this.createPolarChartOptions();

            case "pie":
                return this.createPieChartOptions(showSeriesLegend);

            default:
                return this.createBaseChartOptions(chartType, showSeriesLegend);
        }
    }

    createPolarChartOptions(): Highcharts.Options {
        var options: Highcharts.Options = {
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            },
            title: null,
            legend:
            {
                enabled: false
            },
            plotOptions: {},
            chart: {
                polar: true,
                type: 'line',
                backgroundColor: "transparent",
                borderWidth: 0,
                borderColor: 'transparent',
                resetZoomButton: {
                    theme: {
                        display: "none"
                    }
                }
            }
        };

        return options;
    }

    createSparklineOptions(): Highcharts.Options {
        return {
            chart: {
                //renderTo: (options.chart && options.chart.renderTo) || (hasRenderToArg && a),
                backgroundColor: null,
                borderWidth: 0,
                type: 'area',
                margin: [2, 0, 2, 0],
                width: 120,
                height: 20,
                style: {
                    overflow: 'visible'
                },
                // small optimalization, saves 1-2 ms each sparkline
                //skipClone: true
            },
            title: {
                text: ''
            },
            credits: {
                enabled: false
            },
            xAxis: {
                labels: {
                    enabled: false
                },
                title: {
                    text: null
                },
                startOnTick: false,
                endOnTick: false,
                tickPositions: []
            },
            yAxis: {
                endOnTick: false,
                startOnTick: false,
                labels: {
                    enabled: false
                },
                title: {
                    text: null
                },
                tickPositions: [0]
            },
            legend: {
                enabled: false
            },
            tooltip: {
                hideDelay: 0,
                outside: true,
                shared: true
            },
            plotOptions: {
                series: {
                    animation: false,
                    lineWidth: 1,
                    shadow: false,
                    states: {
                        hover: {
                            lineWidth: 1
                        }
                    },
                    marker: {
                        radius: 1,
                        states: {
                            hover: {
                                radius: 2
                            }
                        }
                    },
                    //fillOpacity: 0.25
                },
                column: {
                    negativeColor: '#910000',
                    borderColor: 'silver'
                }
            }
        }
    }

    createPieChartOptions(showSeriesLegend: boolean): Highcharts.Options {

        const options = this.createBaseChartOptions("pie", showSeriesLegend);

        options.plotOptions.pie = {
            borderColor: 'rgba(0,0,0,0.5)',
            innerSize: '65%',
            dataLabels: {
                enabled: false, // hide on small charts
                format: '<b>{point.name}</b>: {point.percentage:.1f} %',
                style: {
                    color: /*(Highcharts.theme && Highcharts.theme.contrastTextColor) ||*/ 'black'
                }
            },
            showInLegend: true
        };

        options.chart.marginLeft = 10;

        return options;
    }

    createLineChartOptions(showSeriesLegend: boolean): Highcharts.Options {

        const options = this.createBaseChartOptions("line", showSeriesLegend);

        options.chart.spacing = [0, 0, 5, 0];
        options.chart.plotBackgroundColor = "#fff";
        options.chart.plotBorderWidth = 0;
        options.chart.plotBorderColor = "rgba(0,0,0,0.12)";
        options.chart.zoomType = "x";

        options.tooltip = {
            crosshairs: true,
            shared: true
        };

        options.xAxis = {
            id: `xAxis${Math.round(Math.random() * 1000)}`,
            minPadding: 0,
            maxPadding: 0,
            type: "datetime",
            plotBands: [],
            startOnTick: true,
            endOnTick: true,
            minRange: 3600000
        };

        options.yAxis = {
            id: `yAxis${Math.round(Math.random() * 1000)}`,
            title: null,
            labels: {
                enabled: true,
                align: "left",
                x: 3,
                y: 15,
                reserveSpace: false,
                style: {
                    color: "rgba(0,0,0,0.4)"
                }
            }
        };

        options.plotOptions.line = this.getDefaultLineChartConfig();
        options.plotOptions.spline = options.plotOptions.line;
        options.plotOptions.areaspline = this.getDefaultAreaChartConfig();

        return options;
    }

    createColumnChartOptions(showSeriesLegend: boolean): Highcharts.Options {

        const options = this.createBaseChartOptions("column", showSeriesLegend);

        //options.chart.spacing = [0, 0, 5, 0];
        options.chart.spacingLeft = 0;
        options.chart.spacingBottom = 0;

        //options.chart.plotBackgroundColor = "#fff";
        //options.chart.plotBorderWidth = 0;
        options.chart.plotBorderColor = "rgba(0,0,0,0.12)";
        //options.chart.zoomType = "x";

        options.tooltip = {
            crosshairs: true,
            shared: true
        };

        options.xAxis = {
            id: `xAxis${Math.round(Math.random() * 1000)}`,
            //minPadding: 0,
            //maxPadding: 0,
            type: "datetime",
            plotBands: [],
            //minRange: 3600000
        };

        options.yAxis = {
            id: `yAxis${Math.round(Math.random() * 1000)}`,
            title: null,
            labels: {
                enabled: true,
                //align: "left",
                //x: 3,
                //y: 15,
                //reserveSpace: false,
                style: {
                    color: "rgba(0,0,0,0.4)"
                }
            }
        };

        //options.plotOptions.line = this.getDefaultLineChartConfig();
        //options.plotOptions.spline = options.plotOptions.line;
        //options.plotOptions.areaspline = this.getDefaultAreaChartConfig();

        return options;
    }

    //createBaseChartConfig(options: Highcharts.Options, series: Highcharts.IndividualSeriesOptions[], onLoadPromise: angular.IDeferred<ChartObject> = null): HighChartsNGConfig {

    //    var config: HighChartsNGConfig = {
    //        options: options,
    //        series: series,
    //        loading: false,
    //        func: (chart: ChartObject) => {
    //            if (onLoadPromise) {
    //                onLoadPromise.resolve(chart);
    //            }

    //            //chart.redraw();
    //        },
    //        size: {}
    //    };

    //    return config;
    //}

    roundValue(value: number, precision = 1): number {
        var multiplier = Math.pow(10, precision);

        value = value * multiplier;
        value = Math.round(value);
        value /= multiplier

        return value;
    }

    createBasicXRangeChart(xrangeSeries: any): Highcharts.Options {

        var config = this.createXRangeChartOptions();
        config.series = xrangeSeries;

        return config;
    }

    createXRangeChartOptions() {
        var options: any = {
            chart: {
                type: 'xrange',
                spacing: [0, 0, 5, 0],
                backgroundColor: "transparent",
                plotBackgroundColor: "#fff",
                plotBorderWidth: 0,
                plotBorderColor: "rgba(0,0,0,0.12)",
                zoomType: "x",
                resetZoomButton: {
                    position: {
                        align: "right",
                        verticalAlign: "bottom",
                        x: -5,
                        y: 3
                    }
                }
            },
            credits: {
                enabled: false
            },
            plotOptions: {
                series: {
                    turboThreshold: 0,
                }
            },
            tooltip: {
                enabled: false
            },
            title: {
                text: null
            },
            xAxis: {
                type: 'datetime',
                minPadding: 0,
                maxPadding: 0,
                startOnTick: true,
                crosshairs: {
                    enabled: true,
                    shared: true
                }
            },
            yAxis: {
                title: {
                    text: '',
                },
                labels: {
                    align: 'right',
                    x: -19,
                    y: 5,
                    distance: 20,
                    reserveSpace: true,
                    style: {
                        color: 'rgba(0,0,0,0.8)',
                    },
                },
                categories: ["category 1", "category 2", "category 3", "category 4",],
                reversed: true,
            },
        };

        return options;
    }

    getDefaultAreaChartConfig(lineWidth = 1): Highcharts.AreaChart {
        var defaultAreaLineChartConfig = this.getDefaultLineChartConfig(lineWidth);

        //window["Highcharts"].getOptions().colors[0];
        var primaryColour = window["Highcharts"].getOptions().colors[1];
        primaryColour = "rgba(55, 175, 234,1)";
        primaryColour = "rgba(0,0,0,0.05)";

        defaultAreaLineChartConfig.fillColor = this.getGradientFade(primaryColour);

        defaultAreaLineChartConfig.lineWidth = 2;

        return defaultAreaLineChartConfig;
    }

    getGradientFade(colour: string, initialOpacity = 0.08): Highcharts.Gradient {
        var highChartsColor = window["Highcharts"].Color(colour) as Gradient;
        var stop1Colour = highChartsColor.setOpacity(initialOpacity).get('rgba');
        var stop2Colour = highChartsColor.setOpacity(initialOpacity / 5).get('rgba');

        return {
            linearGradient: {
                x1: 0,
                y1: 0,
                x2: 0,
                y2: 1
            },
            stops: [
                //#37afea / 55, 175, 234
                [0, stop1Colour],
                [1, stop2Colour]
            ]
        }
    }

    useVigetColours(config: Highcharts.Options, numDimensions = this.colourService.vigetColours.length) {
        if (!config.colors) {
            config.colors = [];
        }

        for (var i = 0; i < numDimensions; i++) {

            const colour = this.colourService.getVigetColor(i, numDimensions/*, colourOffset*/);

            config.colors.push(colour);
        }
    }

    // ----------------------

    private getDefaultLineChartConfig(lineWidth = 1): Highcharts.AreaChart {
        return {
            marker:
            {
                enabled: false, // disable for simplified view
                radius: 2
            },
            states: {
                hover: {
                    lineWidth: lineWidth
                }
            },
            lineWidth: lineWidth,
            zoneAxis: "x",
            zones: []
        };
    }

    // Fancy tooltip html

    //    tooltip: {
    //        crosshairs: true,
    //        shared: true,
    //        //backgroundColor: null,
    //        formatter: () => { return false; } // disables tooltip but keeps crosshair
    //        //useHTML: true,
    //        //positioner: function (boxWidth, boxHeight, point) {
    //        //    var x = point.plotX + this.chart.plotLeft - (boxWidth / 2);

    //        //    var tooltipMinX = 20;
    //        //    var tooltipMaxX = this.chart.plotLeft + this.chart.plotSizeX - boxWidth + 20;

    //        //    if (x < tooltipMinX) {
    //        //        x = tooltipMinX;
    //        //    }

    //        //    if (x > tooltipMaxX) {
    //        //        x = tooltipMaxX;
    //        //    }

    //        //    var y = -boxHeight-20 /*+ 60*/;

    //        //    return { x: x, y: y };
    //        //},
    //        //borderRadius: 0,
    //        //hideDelay: 30 * 1000,
    //        //padding: 0,
    //        //borderWidth: 0,
    //        //shadow: false,
    //        //formatter: function () {
    //        //    if (controller.lastGraphTooltipX === this.x && controller.lastGraphTooltip && controller.lastGraphTooltip.length) {
    //        //        console.log("hide one");
    //        //        return controller.lastGraphTooltip;
    //        //    }

    //        //    controller.lastGraphTooltipX = this.x;

    //        //    var speedingClass = controller.highlightedPlot.VelocityMph >
    //        //        controller.highlightedPlot.SpeedLimit ? "speeding" : "";

    //        //    // Header
    //        //    var tooltip = `<div class="veri-card plot-hover-info-window flex-none highcharts-tooltip">
    //        //                    <div class="title md-padding layout-row" layout="row">
    //        //                        <span>${controller.veridate.formatDate(this.x)}</span>
    //        //                        <span flex="" class="flex"></span>

    //        //                        <div class="speed-limit current ${speedingClass}">${controller.highlightedPlot.VelocityMph}</div>
    //        //                        <div class="speed-limit">${controller.highlightedPlot.SpeedLimit}</div>
    //        //                    </div>`;

    //        //    // Body
    //        //    tooltip += `<div class="md-padding">`;

    //        //    controller.events.forEach(event => {
    //        //        if (event.plot === controller.highlightedPlot) {
    //        //            console.log(event.options.labelContent);
    //        //            tooltip += `<div class="marker-event small" style="float:right;">${event.options.labelContent}</div>`;
    //        //        }
    //        //    });

    //        //    tooltip += `<div class="ellipsis"><i class="material-icons s16">place</i> ${controller.highlightedPlot.FormattedAddress}</div><br />`;
    //        //    tooltip += `<div class="ellipsis"><i class="material-icons s16"></i> Axle Weight: 0</div>`;
    //        //    tooltip += `<div class="ellipsis"><i class="material-icons s16"></i> % Time Over Torque: 0</div>`;
    //        //    tooltip += `<div class="ellipsis"><i class="material-icons s16"></i> % Time Over Revving: 0</div>`;
    //        //    tooltip += `<div class="ellipsis"><i class="material-icons s16"></i> Elevation: 0</div>`;

    //        //    tooltip += `</div>`;

    //        //    tooltip += `</div>`;

    //        //    controller.lastGraphTooltip = tooltip;

    //        //    return tooltip;

    //        //}
    //    },






    //usePercentageTooltipFormatter(options: Highcharts.Options) {
    //    options.tooltip.formatter = function () {
    //        console.log("formatter", this);
    //        //const minutes = $ctrl.$filter<any>("minutesToDuration")(this.y / 1000);

    //        //this.percentage

    //        if (this.points) {
    //            var output = "";

    //            this.points.forEach(point => {
    //                output += `${point.series.name}: ${Math.round(point.y)}`;
    //            });

    //            return output;
    //        }

    //        return `<h3>${this.key} (${Math.round(this.y)}%)</h3>`;
    //    };

    //    options.tooltip.useHTML = true;
    //}
}
